import { useEffect } from 'react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';
import { REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp';

interface InputOTPPatternProps {
  value: string;
  onChange: (value: string) => void;
  onResend?: () => void;
  timeRemaining: number;
  onTimeUpdate?: (time: number) => void;
}

export function InputOTPPattern({
  value,
  onChange,
  onResend,
  timeRemaining,
  onTimeUpdate,
}: InputOTPPatternProps) {
  useEffect(() => {
    if (timeRemaining <= 0) return;

    const timer = setInterval(() => {
      const newTime = timeRemaining - 1;
      if (newTime <= 0) {
        clearInterval(timer);
        onTimeUpdate?.(0);
      } else {
        onTimeUpdate?.(newTime);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, onTimeUpdate]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResend = () => {
    onChange('');
    onResend?.();
  };

  return (
    <div className="w-full space-y-3 mt-3">
      {/* Timer and Status Bar */}
      <div className="flex items-center justify-between text-[14px]">
        <div className="font-medium text-auth-text">
          Time Remaining <span>{formatTime(timeRemaining)}</span>
        </div>
        <div className="flex items-center gap-2">
          {timeRemaining > 0 && (
            <span className="font-medium text-auth-error">Not Received OTP?</span>
          )}
          <button onClick={handleResend} className="font-medium underline" type="button">
            Resend
          </button>
        </div>
      </div>

      {/* OTP Input */}
      <InputOTP
        maxLength={6}
        pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
        className="w-full"
        value={value}
        onChange={onChange}
      >
        <InputOTPGroup className="flex justify-between w-full gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <InputOTPSlot
              key={i}
              index={i}
              className={`
                w-[48px] h-[40px] text-xl font-semibold transition-all duration-200 
                bg-auth-inputBg text-auth-inputText
                !outline-none !ring-0 !ring-offset-0
                ${
                  value[i]
                    ? '!border-2 !border-green-500 !rounded-[10px]'
                    : '!border-2 !border-red-500 !rounded-[10px]'
                }
              `}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
    </div>
  );
}
